import { Button, Icon, Spacing, TextInput } from 'zens-storybook'
import React, { Suspense, startTransition, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { StyleSheet, TouchableOpacity, View } from 'react-native'
import { AccessType, StorageKey, WorkspacesFilter, useStore } from 'zens-store'
import { useNumberParams, usePaginationParam, useSortParam, useFilterParam, Link } from 'zens-router'
import { useBreadcrumb, usePermissions } from '@dms/hook'

import { WorkspaceListHeader, WorkspaceTable } from '@dms/component'
import { debounce } from 'lodash'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    flex: 1,
    margin: Spacing.L,
  },
  header: {
    flexDirection: 'row',
  },
  search: {
    minWidth: 300,
    marginRight: Spacing.S,
  },
})

const ScreenWorkspaceList = () => {
  const { t } = useTranslation()
  const [{ companyId }] = useNumberParams()
  const [pagination, setPagination] = usePaginationParam()
  const [sort, setSort] = useSortParam({ field: 'name', direction: 'asc' })
  const { storage } = useStore()
  const data = storage.get(`${companyId}:${StorageKey.WorkspaceFilter}`, { session: true })
  const defaultValue = data ? JSON.parse(data) : undefined
  const [filter, setFilter] = useFilterParam<WorkspacesFilter>(defaultValue)
  const [search, setSearch] = useState<string>('')
  const [filterSearch, setFilterSearch] = useState<string>(search)
  const debouncedSearch = useRef(debounce((value) => startTransition(() => setFilterSearch(value)), 300)).current
  const { hasPermission } = usePermissions(companyId)

  const breadcrumb = useBreadcrumb([
    {
      title: t('Workspaces'),
    },
  ])

  function updateSearch(value: string) {
    setSearch(value)
    setPagination({ page: 0, size: pagination.size })
    debouncedSearch(value)
  }

  function updateFilter(filter: WorkspacesFilter) {
    setFilter(filter)
    setPagination({ page: 0, size: pagination.size })
  }

  const Header = (
    <View style={styles.header}>
      <TextInput
        left="search"
        placeholder={t('Search workspace name')}
        right={
          search ? (
            <TouchableOpacity onPress={() => updateSearch('')} testID="WorkspaceList.Search.Clear">
              <Icon name="close" size="S" />
            </TouchableOpacity>
          ) : undefined
        }
        value={search || ''}
        onChangeText={updateSearch}
        style={styles.search}
        testID="WorkspaceList.Search"
      />
      {hasPermission(AccessType.Update, 'workspace') && // For now hide the add button also when user has no update permission
        hasPermission(AccessType.Create, 'workspace') && (
          <Link href={`/companies/${companyId}/workspaces/add`} asChild>
            <Button title={t('Add workspace')} left="plus" testID="WorkspaceList.Add" />
          </Link>
        )}
    </View>
  )

  return (
    <View style={styles.container}>
      <Suspense fallback={<WorkspaceListHeader.Skeleton />}>
        <WorkspaceListHeader
          breadcrumb={breadcrumb}
          companyId={companyId}
          pagination={pagination}
          sort={sort}
          filter={{ ...filter, search: filterSearch }}
          accessory={Header}
        />
      </Suspense>
      <View style={styles.content}>
        <Suspense fallback={<WorkspaceTable.Skeleton filter />}>
          <WorkspaceTable
            companyId={companyId}
            pagination={pagination}
            onPaginationChange={setPagination}
            sort={sort}
            onSortChange={setSort}
            filter={{ ...filter, search: filterSearch }}
            onFilterChange={updateFilter}
            addHref={`/companies/${companyId}/workspaces/add`}
            selectHref={hasPermission(AccessType.Read, 'workspaceDetail') ? (workspaceId) => `/companies/${companyId}/workspaces/${workspaceId}` : undefined}
            typeHref={(type) =>
              type === 'table'
                ? {
                    pathname: `/companies/${companyId}/workspaces`,
                    params: { filter: JSON.stringify(filter) },
                  }
                : {
                    pathname: `/companies/${companyId}/workspaces/floorplan`,
                    params: { filter: JSON.stringify(filter) },
                  }
            }
          />
        </Suspense>
      </View>
    </View>
  )
}

export default ScreenWorkspaceList
